// ============================================================================
// CHANGO V5.0 — Welcome Page (§9.6.2c 阶段 2 Step 1：选套餐)
// ============================================================================
// 落地页，强制选 1 个套餐才能继续。
// 双栏（A 系蓝底 5 张 + B 系绿底 4 张）+ 加购按钮
// 调 GET /api/billing/skus 拿价格（含 original_amount_rmb 限时特价）
// 用户选中一张卡片 → 高亮 + "下一步"按钮可点 → 跳 /app/activate-trial?sku_code=xxx
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import { A_SERIES, B_SERIES, SERIES_COLORS, brandForSku } from '../../lib/skuBrandVersion.js';

export default function Welcome() {
  const navigate = useNavigate();
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);

  useEffect(() => {
    const fetchSkus = async () => {
      try {
        setLoading(true);
        const data = await api.get('/api/billing/skus');
        const packageSkus = Array.isArray(data?.skus)
          ? data.skus.filter((s) => !s.is_addon && s.sku_code !== 'extra_seat')
          : [];
        setSkus(packageSkus);
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : '加载套餐失败';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchSkus();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-chango-500 border-t-transparent" aria-hidden="true" />
          <span className="text-sm">加载套餐中…</span>
        </div>
      </div>
    );
  }

  const handleSelectSku = (skuCode) => {
    setSelectedSku(skuCode);
  };

  const handleNext = () => {
    if (!selectedSku) return;
    navigate(`/app/activate-trial?sku_code=${selectedSku}`);
  };

  // 分离 A 系、B 系。首次激活只能选择 9 个套餐，extra_seat 走席位加购流。
  const aSeriesSkus = skus.filter(s => A_SERIES.includes(s.sku_code));
  const bSeriesSkus = skus.filter(s => B_SERIES.includes(s.sku_code));

  const renderSkuCard = (sku) => {
    const brand = brandForSku(sku.sku_code);
    const colors = SERIES_COLORS[brand.series] || SERIES_COLORS._;
    const isSelected = selectedSku === sku.sku_code;
    const originalPrice = sku.original_amount_rmb ? parseFloat(sku.original_amount_rmb) : null;
    const currentPrice = parseFloat(sku.amount_rmb || 0);
    const isDiscounted = originalPrice && originalPrice > currentPrice;

    return (
      <button
        key={sku.sku_code}
        type="button"
        onClick={() => handleSelectSku(sku.sku_code)}
        className={`
          relative rounded-lg border-2 p-6 text-left transition-all
          ${isSelected ? `${colors.card} border-4` : `${colors.card} border-2`}
          hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-chango-500
        `}
      >
        {/* 系列徽章 */}
        <div className={`mb-2 inline-block rounded ${colors.badge} px-2 py-1 text-xs font-semibold`}>
          {brand.displayCode || '加购'}
        </div>

        {/* 套餐名 */}
        <h3 className={`mb-2 text-lg font-semibold ${colors.accent}`}>{brand.shortName}</h3>

        {/* 描述 */}
        <p className="mb-4 text-sm text-slate-600">{brand.description}</p>

        {/* 价格 */}
        <div className="mb-4 flex items-baseline gap-2">
          {isDiscounted && (
            <span className="text-sm line-through text-slate-500">
              ¥{originalPrice?.toFixed(0)}
            </span>
          )}
          <span className="text-2xl font-bold text-chango-600">
            ¥{currentPrice.toFixed(0)}
          </span>
          <span className="text-sm text-slate-500">/年</span>
        </div>

        {/* 限时特价标签 */}
        {isDiscounted && (
          <div className="mb-3 inline-block rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
            限时特价
          </div>
        )}

        {/* 模块数和运营主体层 */}
        <div className="mb-3 text-xs text-slate-600">
          <p className="mb-1">
            <span className="font-medium">{brand.moduleCount}</span> 个模块 ·{' '}
            <span className="font-medium">{brand.hasELayer ? '含运营主体层' : '单主体'}</span>
          </p>
          <p>6 免费席位 · 超出 ¥999/席/年</p>
        </div>

        {/* 核心场景 */}
        <div className="mb-3 border-t border-slate-300 pt-3">
          <p className="text-xs font-semibold text-slate-700 mb-1">核心场景</p>
          <ul className="text-xs text-slate-600 space-y-1">
            {brand.useCase.slice(0, 2).map((u, i) => (
              <li key={i}>• {u}</li>
            ))}
          </ul>
        </div>

        {/* 选中指示 */}
        {isSelected && (
          <div className="absolute top-2 right-2">
            <svg
              className="h-6 w-6 text-chango-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto w-full max-w-7xl px-4">
        <h1 className="mb-4 text-3xl font-bold text-slate-800">选择套餐</h1>
        <p className="mb-12 text-slate-600">
          选择最适合您的套餐，立即开始 7 天免费试用。
        </p>

        {error ? (
          <div role="alert" className="alert-error mb-8">{error}</div>
        ) : null}

        {/* A 系（工厂）*/}
        <div className="mb-12">
          <h2 className="mb-6 flex items-center gap-2 text-xl font-semibold text-slate-800">
            <span className={`${SERIES_COLORS.A.dot} h-3 w-3 rounded-full`} />
            A 系 — 工厂版（含工厂模块）
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {aSeriesSkus.map(renderSkuCard)}
          </div>
        </div>

        {/* B 系（外贸）*/}
        <div className="mb-12">
          <h2 className="mb-6 flex items-center gap-2 text-xl font-semibold text-slate-800">
            <span className={`${SERIES_COLORS.B.dot} h-3 w-3 rounded-full`} />
            B 系 — 外贸版（无工厂模块）
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
            {bSeriesSkus.map(renderSkuCard)}
          </div>
        </div>

        {/* 底部操作按钮 */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex items-center justify-between gap-4">
          <p className="text-sm text-slate-600">
            {selectedSku
              ? `已选择 ${brandForSku(selectedSku).shortName}`
              : '请选择一个套餐以继续'}
          </p>
          <button
            type="button"
            onClick={handleNext}
            disabled={!selectedSku}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一步
          </button>
        </div>
      </div>
    </div>
  );
}
